import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../context/I18nContext";
import { api, API } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { LogIn, UserPlus, Clock, ShieldAlert, Send, RefreshCw } from "lucide-react";

const PLATFORM = { windows: "Laptop (Windows)", android: "Android" };

function DeviceBlock({ info, email, password, onBack, onRetry, busy }) {
  const hw = info.hwid;
  const [sent, setSent] = useState(!!info.sent);
  const [sending, setSending] = useState(false);
  const sendHwid = async () => {
    setSending(true);
    try {
      const r = await api.post("/auth/device/request", { email, password });
      if (r.data?.status === "approved") { onRetry(); return; }
      setSent(true);
      toast.success("HWID terkirim ke admin", { description: "Tunggu aktivasi, lalu klik CEK AKTIVASI." });
    } catch (e) {
      toast.error(errMsg(e?.response?.data?.detail?.message || e?.response?.data?.detail, "Gagal mengirim HWID"));
    } finally { setSending(false); }
  };
  return (
    <div data-testid="login-device-block" className="rounded-lg bg-amber-950/30 border border-amber-800/40 p-4 text-center">
      <ShieldAlert className="h-7 w-7 text-amber-400 mx-auto mb-2" />
      <p data-testid="login-device-message" className="text-sm text-amber-200">{sent && hw ? "HWID sudah terkirim. Menunggu admin mengaktifkan perangkat ini." : info.message}</p>
      {hw && (
        <>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-4">HWID {PLATFORM[info.platform] || ""}</div>
          <div data-testid="login-device-hwid" className="mt-1 font-mono-tt text-lg font-bold text-white select-all">{hw}</div>
          <div className="grid grid-cols-2 gap-2 mt-4">
            <Button data-testid="login-device-send" onClick={sendHwid} disabled={sending || sent} className="bg-red-600 hover:bg-red-500 text-white disabled:opacity-60"><Send className="h-4 w-4 mr-1.5" />{sent ? "Terkirim" : sending ? "..." : "Kirim HWID"}</Button>
            <Button data-testid="login-device-check" onClick={onRetry} disabled={busy} className="bg-emerald-600 hover:bg-emerald-500 text-white"><RefreshCw className="h-4 w-4 mr-1.5" />Cek Aktivasi</Button>
          </div>
        </>
      )}
      {(info.code === "app_required" || info.code === "app_outdated") && (
        <a data-testid="login-download-windows" href={`${API}/app-install/download`} className="block mt-4 rounded-md px-3 py-2 text-sm font-semibold bg-red-600 hover:bg-red-500 text-white">Unduh TRMD Windows</a>
      )}
      <Button data-testid="login-device-back" onClick={onBack} className="mt-3 w-full bg-slate-800 hover:bg-slate-700 text-slate-100">Kembali</Button>
    </div>
  );
}
import { LanguageSwitcher } from "../components/LanguageSwitcher";

function errMsg(detail, fallback) {
  if (detail == null) return fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function Login() {
  const { loginPw, register } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [device, setDevice] = useState(null);
  const [f, setF] = useState({ name: "", phone: "", email: "", country: "", password: "", confirm: "" });
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  // Hidden admin entry: press & hold the logo ~700ms to open the separate admin login.
  const holdRef = useRef(null);
  const startHold = () => { holdRef.current = setTimeout(() => navigate("/admin-login"), 700); };
  const cancelHold = () => { if (holdRef.current) { clearTimeout(holdRef.current); holdRef.current = null; } };

  const doLogin = async () => {
    setBusy(true);
    try {
      const u = await loginPw(f.email.trim(), f.password);
      toast.success(t("login.toastSuccess"));
      navigate(window.tiroDesktopApp ? "/workspace" : u?.is_admin ? "/admin/users" : "/map-editor", { replace: true });
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d && typeof d === "object" && d.code) setDevice(d);
      else { setDevice(null); toast.error(errMsg(d, t("login.err")) || e.message); }
    } finally { setBusy(false); }
  };

  const doRegister = async () => {
    if (f.password !== f.confirm) { toast.error(t("login.toastPwMismatch")); return; }
    setBusy(true);
    try {
      await register({ name: f.name.trim(), phone: f.phone.trim(), email: f.email.trim(), country: f.country.trim(), password: f.password });
      setPending(true);
      toast.success(t("login.toastRegistered"));
    } catch (e) {
      toast.error(errMsg(e?.response?.data?.detail, t("login.err")) || e.message);
    } finally { setBusy(false); }
  };

  const doForgot = async () => {
    if (!f.name.trim() || !f.email.trim()) { toast.error(t("login.toastForgotFill")); return; }
    setBusy(true);
    try {
      const r = await api.post("/auth/forgot", { name: f.name.trim(), email: f.email.trim(), phone: f.phone.trim() });
      toast.success(r?.data?.message || t("login.toastForgotSent"));
      setShowForgot(false);
    } catch (e) {
      toast.error(errMsg(e?.response?.data?.detail, t("login.err")) || e.message);
    } finally { setBusy(false); }
  };

  const field = (k, label, type = "text", ph = "") => (
    <div className="space-y-1.5">
      <Label className="text-xs uppercase tracking-widest text-slate-400">{label}</Label>
      <Input data-testid={`login-${k}`} type={type} value={f[k]} onChange={set(k)} placeholder={ph}
        className="bg-[#121824] border-slate-700 text-slate-100 focus-visible:ring-cyan-400" />
    </div>
  );

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#07090e] p-4">
      <div className="w-full max-w-md rounded-2xl border border-red-900/40 bg-[#0c1017] p-7 relative overflow-hidden">
        <div className="flex items-center justify-between mb-6">
          <div data-testid="login-admin-hold" className="flex items-center gap-3 select-none"
            onMouseDown={startHold} onMouseUp={cancelHold} onMouseLeave={cancelHold}
            onTouchStart={startHold} onTouchEnd={cancelHold}>
            {window.tiroDesktopApp ? (
              <img data-testid="login-trmd-logo" src="/trmd-logo.webp" alt="TRMD" className="h-14 w-auto max-w-[220px] object-contain" />
            ) : (<>
            <img src="/trmd-icon.png" alt="TRMD Performance" className="h-12 w-12 rounded-xl object-cover shadow-lg shadow-cyan-400/30" />
            <div>
              <div className="font-head font-black text-2xl text-white leading-none">TRMD Performance</div>
              <div className="text-[11px] uppercase tracking-widest text-slate-500 mt-1">{t("login.tagline")}</div>
            </div>
            </>)}
          </div>
          <LanguageSwitcher showIcon={false} />
        </div>

        {device ? (
          <DeviceBlock info={device} email={f.email.trim()} password={f.password} busy={busy} onRetry={doLogin} onBack={() => setDevice(null)} />
        ) : pending ? (
          <div data-testid="login-pending" className="rounded-lg bg-amber-950/30 border border-amber-800/40 p-4 text-center">
            <Clock className="h-7 w-7 text-amber-400 mx-auto mb-2" />
            <div className="font-bold text-amber-300">{t("login.pendingTitle")}</div>
            <p className="text-xs text-slate-400 mt-1">{t("login.pendingBody", { email: f.email })}</p>
            <Button data-testid="login-back" onClick={() => { setPending(false); setMode("login"); }} className="mt-4 w-full bg-slate-800 hover:bg-slate-700 text-slate-100">{t("login.backToLogin")}</Button>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-1 p-1 rounded-lg bg-[#121824] mb-5">
              <button data-testid="login-tab-login" onClick={() => setMode("login")} className={`py-2 rounded text-sm font-bold ${mode === "login" ? "bg-cyan-500 text-slate-950" : "text-slate-400"}`}><LogIn className="h-4 w-4 inline mr-1.5" />{t("login.tabLogin")}</button>
              <button data-testid="login-tab-register" onClick={() => setMode("register")} className={`py-2 rounded text-sm font-bold ${mode === "register" ? "bg-cyan-500 text-slate-950" : "text-slate-400"}`}><UserPlus className="h-4 w-4 inline mr-1.5" />{t("login.tabRegister")}</button>
            </div>

            <div className="space-y-3">
              {mode === "register" && field("name", t("login.name"))}
              {mode === "register" && field("phone", t("login.phone"), "tel", "+62812xxxx")}
              {field("email", t("login.email"), "email", "name@email.com")}
              {mode === "register" && field("country", t("login.country"))}
              {field("password", t("login.password"), "password")}
              {mode === "register" && field("confirm", t("login.confirm"), "password")}

              <Button data-testid="login-submit" onClick={mode === "login" ? doLogin : doRegister} disabled={busy}
                className="w-full bg-gradient-to-r from-cyan-400 to-teal-500 hover:from-cyan-300 hover:to-teal-400 text-slate-950 font-bold mt-1">
                {busy ? "..." : mode === "login" ? t("login.submitLogin") : t("login.submitRegister")}
              </Button>

              {mode === "login" && (
                <div className="pt-1">
                  <button data-testid="login-forgot-toggle" onClick={() => setShowForgot((v) => !v)}
                    className="block w-full text-center text-xs text-slate-400 hover:text-cyan-300 transition">
                    {t("login.forgotQ")} <span className="font-semibold text-cyan-300">{t("login.forgotAction")}</span>
                  </button>
                  {showForgot && (
                    <div className="mt-3 space-y-3 rounded-lg border border-slate-800 bg-[#0a0e14] p-3" data-testid="login-forgot-form">
                      <p className="text-xs text-slate-500">{t("login.forgotHint")}</p>
                      {field("name", t("login.name"))}
                      {field("email", t("login.email"), "email", "name@email.com")}
                      {field("phone", t("login.phone"), "tel", "+62812xxxx")}
                      <Button data-testid="login-forgot-submit" onClick={doForgot} disabled={busy}
                        className="w-full bg-gradient-to-r from-cyan-400 to-teal-500 text-slate-950 font-bold">
                        {busy ? "..." : t("login.forgotSubmit")}
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
